import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Invoice } from '@coreledger/shared-types';
import { invoicesApi } from '../api/invoices';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';
import { useIsTransport } from '../hooks/useIsTransport';

export interface LocalTemplate {
  id: string;
  name: string;
  description: string;
  invoiceData: Partial<Invoice>;
  createdAt: string;
}

interface RecurringSchedule {
  id: string;
  templateId: string;
  templateName: string;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  nextDue: string;
  isActive: boolean;
  createdAt: string;
}

const STORAGE_KEY = 'cl_templates';
const SCHEDULE_KEY = 'cl_schedules';
const FREQ_LABELS = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

export function loadTemplates(): LocalTemplate[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as LocalTemplate[]; }
  catch { return []; }
}

export function saveTemplate(t: LocalTemplate): void {
  const list = loadTemplates().filter(x => x.id !== t.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([t, ...list]));
}

export function deleteTemplate(id: string): void {
  const list = loadTemplates().filter(x => x.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function loadSchedules(): RecurringSchedule[] {
  try { return JSON.parse(localStorage.getItem(SCHEDULE_KEY) ?? '[]') as RecurringSchedule[]; }
  catch { return []; }
}

function saveSchedule(s: RecurringSchedule): void {
  const list = loadSchedules().filter(x => x.id !== s.id);
  localStorage.setItem(SCHEDULE_KEY, JSON.stringify([s, ...list]));
}

function deleteSchedule(id: string): void {
  const list = loadSchedules().filter(x => x.id !== id);
  localStorage.setItem(SCHEDULE_KEY, JSON.stringify(list));
}

const DOC_LABELS: Record<string, string> = {
  tax_invoice: 'Tax Invoice', bill_of_supply: 'Bill of Supply',
  quotation: 'Quotation', credit_note: 'Credit Note',
  debit_note: 'Debit Note', lorry_receipt: 'Lorry Receipt',
};

const today = () => new Date().toISOString().slice(0, 10);

function advanceDue(date: string, freq: RecurringSchedule['frequency']): string {
  const d = new Date(date);
  if (freq === 'weekly') d.setDate(d.getDate() + 7);
  else if (freq === 'monthly') d.setMonth(d.getMonth() + 1);
  else if (freq === 'quarterly') d.setMonth(d.getMonth() + 3);
  else d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { canDelete } = useRole();
  const isTransport = useIsTransport();
  const [templates, setTemplates] = useState<LocalTemplate[]>(loadTemplates);
  const [schedules, setSchedules] = useState<RecurringSchedule[]>(loadSchedules);
  const [search, setSearch] = useState('');

  // Schedule modal state
  const [generating, setGenerating] = useState<string | null>(null);
  const [genMsg, setGenMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [schedModal, setSchedModal] = useState<LocalTemplate | null>(null);
  const [schedFreq, setSchedFreq] = useState<RecurringSchedule['frequency']>('monthly');
  const [schedNextDue, setSchedNextDue] = useState(today());

  const autoFiredRef = useRef(false);

  useEffect(() => {
    if (autoFiredRef.current) return;
    autoFiredRef.current = true;
    const dueNow = loadSchedules().filter(s => s.isActive && s.nextDue <= today());
    if (!dueNow.length) return;
    (async () => {
      const names = dueNow.map(s => s.templateName).join(', ');
      const ok = await confirm({
        message: `${dueNow.length} recurring invoice${dueNow.length > 1 ? 's are' : ' is'} due today:\n${names}\n\nGenerate now?`,
      });
      if (!ok) return;
      const created: string[] = [];
      const allTemplates = loadTemplates();
      for (const sched of dueNow) {
        const tmpl = allTemplates.find(t => t.id === sched.templateId);
        if (!tmpl) continue;
        try {
          const inv = await invoicesApi.create({ ...tmpl.invoiceData, date: today() });
          created.push(inv.invoiceNumber ?? 'Invoice');
          saveSchedule({ ...sched, nextDue: advanceDue(sched.nextDue, sched.frequency) });
        } catch { /* skip failed template silently */ }
      }
      refresh();
      if (created.length) setGenMsg({ type: 'ok', text: `Auto-generated: ${created.join(', ')}` });
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function refresh() { setTemplates(loadTemplates()); setSchedules(loadSchedules()); }

  async function handleDelete(id: string, name: string) {
    if (!await confirm({ message: `Delete template "${name}"?`, danger: true })) return;
    deleteTemplate(id);
    // also remove any schedule for this template
    loadSchedules().filter(s => s.templateId === id).forEach(s => deleteSchedule(s.id));
    refresh();
  }

  function handleUse(t: LocalTemplate) {
    sessionStorage.setItem('cl_use_template', JSON.stringify(t.invoiceData));
    navigate('/invoices/new');
  }

  async function handleGenerateNow(t: LocalTemplate) {
    setGenerating(t.id); setGenMsg(null);
    try {
      const inv = await invoicesApi.create({ ...t.invoiceData, date: new Date().toISOString().slice(0, 10) });
      setGenMsg({ type: 'ok', text: `Invoice ${inv.invoiceNumber} created!` });
      setTimeout(() => { setGenMsg(null); navigate(`/invoices/${inv.id}`); }, 1500);
    } catch (err) {
      setGenMsg({ type: 'err', text: err instanceof Error ? err.message : 'Failed to generate invoice' });
    } finally {
      setGenerating(null);
    }
  }

  function openSchedModal(t: LocalTemplate) {
    const existing = schedules.find(s => s.templateId === t.id);
    setSchedFreq(existing?.frequency ?? 'monthly');
    setSchedNextDue(existing?.nextDue ?? today());
    setSchedModal(t);
  }

  function handleSaveSchedule() {
    if (!schedModal) return;
    const existing = schedules.find(s => s.templateId === schedModal.id);
    const s: RecurringSchedule = {
      id: existing?.id ?? Math.random().toString(36).slice(2),
      templateId: schedModal.id,
      templateName: schedModal.name,
      frequency: schedFreq,
      nextDue: schedNextDue,
      isActive: true,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
    };
    saveSchedule(s);
    refresh();
    setSchedModal(null);
  }

  function handleDeleteSchedule(templateId: string) {
    const s = schedules.find(x => x.templateId === templateId);
    if (s) { deleteSchedule(s.id); refresh(); }
  }

  const filtered = templates.filter(t =>
    (isTransport || t.invoiceData.docType !== 'lorry_receipt') &&
    (!search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description ?? '').toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Invoice Templates</h1>
      </div>

      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#0369a1' }}>
        Templates are saved locally on this device. Open any invoice form and click <strong>Save as Template</strong> to create one.
      </div>

      {genMsg && (
        <div className={genMsg.type === 'ok' ? 'success-box' : 'error-box'} style={{ marginBottom: '1rem' }}>{genMsg.text}</div>
      )}

      {(() => {
        const dueToday = schedules.filter(s => s.isActive && s.nextDue <= today());
        if (!dueToday.length) return null;
        return (
          <div className="warn-box" style={{ marginBottom: '1.25rem' }}>
            <span>🔔</span>
            <span style={{ fontWeight: 500 }}>
              <strong>{dueToday.length} recurring invoice{dueToday.length > 1 ? 's' : ''}</strong> due today:{' '}
              {dueToday.map(s => s.templateName).join(', ')}
            </span>
          </div>
        );
      })()}

      {templates.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…" style={{ maxWidth: 320 }} />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">{templates.length === 0 ? 'No templates saved yet' : 'No templates match your search'}</div>
            {templates.length === 0 && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Create or edit an invoice, then click <strong>Save as Template</strong> to save it for reuse.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '1rem' }}>
          {filtered.map(t => {
            const sched = schedules.find(s => s.templateId === t.id);
            return (
              <div key={t.id} className="card" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.2rem' }}>{t.name}</div>
                  {t.description && <div style={{ fontSize: '0.825rem', color: 'var(--text-2)' }}>{t.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {t.invoiceData.docType && (
                    <span className="badge badge-blue">{DOC_LABELS[t.invoiceData.docType] ?? t.invoiceData.docType}</span>
                  )}
                  {t.invoiceData.partyName && (
                    <span className="badge badge-gray">{t.invoiceData.partyName}</span>
                  )}
                  {t.invoiceData.items?.length ? (
                    <span className="badge badge-gray">{t.invoiceData.items.length} item{t.invoiceData.items.length !== 1 ? 's' : ''}</span>
                  ) : null}
                  {sched && (
                    <span className="badge" style={{ background: '#dcfce7', color: '#15803d' }}>
                      🔁 {FREQ_LABELS[sched.frequency]} · due {sched.nextDue}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Saved {t.createdAt.slice(0, 10)}</div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => handleUse(t)}>Use</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleGenerateNow(t)} disabled={generating === t.id} title="Create invoice from this template immediately">
                    {generating === t.id ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '⚡ Now'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => openSchedModal(t)}
                    title={sched ? 'Edit schedule' : 'Set recurring schedule'}
                  >🔁</button>
                  {sched && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteSchedule(t.id)} title="Remove schedule" style={{ color: 'var(--danger)' }}>✕</button>
                  )}
                  {canDelete && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id, t.name)}>Del</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Modal */}
      {schedModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSchedModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">🔁 Recurring Schedule — {schedModal.name}</span>
              <button className="modal-close" onClick={() => setSchedModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
                The app will remind you to generate an invoice from this template when the next due date arrives.
              </p>
              <div className="form-row">
                <label>Frequency
                  <select value={schedFreq} onChange={e => setSchedFreq(e.target.value as RecurringSchedule['frequency'])}>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly (every 3 months)</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </label>
              </div>
              <div className="form-row">
                <label>Next Due Date
                  <input type="date" value={schedNextDue} onChange={e => setSchedNextDue(e.target.value)} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setSchedModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveSchedule}>Save Schedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
